import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ROLES, ROLE_NAMES } from '../constants/roles';

export interface LoginPayload {
  username: string;
  password: string;
}

export interface LoginUser {
  id: string;
  fullname: string;
  username: string;
  roleId: string;
  roleName: string;
  powerStationId?: string;
  powerStationName?: string;
}

export interface LoginResponse {
  status: boolean;
  message: string | string[];
  token: string;
  user: LoginUser;
  data?: unknown[] | Record<string, unknown>;
}

/** Initial login step — credentials validated, OTP sent to registered mobile. */
export interface LoginInitResponse {
  status: boolean;
  message: string | string[];
  data?: unknown[] | Record<string, unknown>;
  token?: string;
  user?: LoginUser;
}

export interface OtpApiResponse {
  status: boolean;
  message: string | string[];
  data?: unknown[] | Record<string, unknown>;
}

export interface VerifyOtpPayload {
  username: string;
  otp: string;
}

export interface ResendOtpPayload {
  username: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly TOKEN_KEY = 'flyash_token';
  private readonly USER_KEY = 'flyash_user';
  private baseUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {}

  login(username: string, password: string): Observable<LoginInitResponse> {
    return this.http.post<LoginInitResponse>(`${this.baseUrl}login`, { username, password });
  }

  verifyLoginOtp(username: string, otp: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.baseUrl}login/verifyOtp`, {
      username,
      otp,
    } satisfies VerifyOtpPayload);
  }

  resendLoginOtp(username: string): Observable<OtpApiResponse> {
    return this.http.post<OtpApiResponse>(`${this.baseUrl}login/resendOtp`, {
      username,
    } satisfies ResendOtpPayload);
  }

  storeSession(response: LoginResponse): void {
    localStorage.setItem(this.TOKEN_KEY, response.token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(response.user));
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getUser(): LoginUser | null {
    const raw = localStorage.getItem(this.USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as LoginUser;
    } catch {
      return null;
    }
  }

  getUserRole(): string | null {
    return this.getUser()?.roleId || null;
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.router.navigate(['/login']);
  }

  /**
   * Returns the dashboard route path for a given role.
   */
  getDashboardRoute(roleId: string, roleName?: string): string {
    if (this.isAshHandlingOfficer(roleId, roleName)) {
      return '/officer';
    }

    switch (roleId) {
      case ROLES.ADMIN:
        return '/admin';
      case ROLES.VENDOR:
        return '/vendor';
      case ROLES.MEMBER_COMMITTEE:
        return '/member';
      case ROLES.CHIEF_ENGINEER:
        return '/chief';
      case ROLES.INSPECTION:
        return '/inspection';
      default:
        return '/login';
    }
  }

  resolveDashboardRoute(user: LoginUser): string {
    return this.getDashboardRoute(user.roleId, user.roleName);
  }

  isAshHandlingOfficer(roleId?: string | null, roleName?: string | null): boolean {
    return roleId === ROLES.ASH_HANDLING_OFFICER || roleName === ROLE_NAMES.ASH_HANDLING;
  }
}
